import type {
  RegisterNotificationDeviceInput,
  UnregisterNotificationDeviceInput,
} from "@rakazo/contracts";
import { NOTIFICATION_DEVICE_ID_PATTERN, WebPushSubscriptionSchema } from "@rakazo/contracts";

/** The stable id this browser registers under. It survives restarts and sign-ins. */
export const WEB_PUSH_DEVICE_ID_KEY = "hrokbot.web-push.device-id";
/** The enable offer can be dismissed like the install offer. */
export const WEB_PUSH_PROMPT_DISMISSED_KEY = "hrokbot.web-push.prompt-dismissed";

export type WebPushUnsupportedReason =
  | "insecure"
  | "no-service-worker"
  | "no-push-manager"
  | "no-notification"
  | "ios-not-installed"
  | "ios-version";

export type WebPushSupport =
  | { supported: true }
  | { supported: false; reason: WebPushUnsupportedReason };

export type WebPushEnvironment = {
  secureContext: boolean;
  serviceWorker: boolean;
  pushManager: boolean;
  notification: boolean;
  userAgent: string;
  /** `display-mode: standalone` or iOS `navigator.standalone`. */
  standalone: boolean;
};

export type WebPushPermission = "default" | "denied" | "granted";

export type WebPushPermissionApi = {
  readonly permission: WebPushPermission;
  requestPermission(): Promise<WebPushPermission>;
};

/** The part of `localStorage` this module uses; a test supplies a plain map. */
export type WebPushStorage = Pick<Storage, "getItem" | "setItem">;

export type WebPushSubscriptionLike = {
  readonly endpoint: string;
  readonly expirationTime: number | null;
  toJSON(): unknown;
  unsubscribe(): Promise<boolean>;
};

export type WebPushPushManager = {
  getSubscription(): Promise<WebPushSubscriptionLike | null>;
  subscribe(options: {
    userVisibleOnly: true;
    applicationServerKey: BufferSource;
  }): Promise<WebPushSubscriptionLike>;
};

export type WebPushRegistration = { readonly pushManager: WebPushPushManager };

export type EnableWebPushDeps = {
  registration: WebPushRegistration;
  permission: WebPushPermissionApi;
  fetchPublicKey(): Promise<string | null>;
  registerDevice(input: RegisterNotificationDeviceInput): Promise<unknown>;
  storage?: WebPushStorage;
  createId?: () => string;
};

export type EnableWebPushResult =
  | { status: "enabled"; deviceId: string }
  | { status: "denied" }
  | { status: "unavailable" }
  | { status: "failed" };

export type DisableWebPushDeps = {
  registration: WebPushRegistration;
  unregisterDevice(input: UnregisterNotificationDeviceInput): Promise<unknown>;
  storage?: Pick<Storage, "getItem">;
};

export type WebPushClientState = "off" | "denied" | "enabled";

function isIosIdentity(environment: Pick<WebPushEnvironment, "userAgent">): boolean {
  if (/iPhone|iPad|iPod/i.test(environment.userAgent)) return true;
  // iPadOS reports a desktop Safari user agent; the PWA check supplies touch support.
  return /Macintosh/i.test(environment.userAgent) && /Mobile/i.test(environment.userAgent);
}

function iosVersion(userAgent: string): { major: number; minor: number } | undefined {
  const match = /OS (\d+)_(\d+)/.exec(userAgent);
  if (!match) return undefined;
  return { major: Number(match[1]), minor: Number(match[2]) };
}

/**
 * Push needs a secure context, a service worker, `PushManager` and `Notification`. On an iPhone
 * it additionally needs the app on the home screen and iOS 16.4 or later: Safari in a tab cannot
 * subscribe, whatever the user does.
 */
export function webPushSupport(environment: WebPushEnvironment): WebPushSupport {
  if (!environment.secureContext) return { supported: false, reason: "insecure" };
  if (isIosIdentity(environment)) {
    if (!environment.standalone) return { supported: false, reason: "ios-not-installed" };
    const version = iosVersion(environment.userAgent);
    if (version && (version.major < 16 || (version.major === 16 && version.minor < 4))) {
      return { supported: false, reason: "ios-version" };
    }
  }
  if (!environment.serviceWorker) return { supported: false, reason: "no-service-worker" };
  if (!environment.pushManager) return { supported: false, reason: "no-push-manager" };
  if (!environment.notification) return { supported: false, reason: "no-notification" };
  return { supported: true };
}

function browserStorage(): Storage | undefined {
  try {
    return typeof localStorage === "undefined" ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

export function readWebPushDeviceId(
  storage: Pick<Storage, "getItem"> | undefined = browserStorage(),
): string | undefined {
  try {
    const value = storage?.getItem(WEB_PUSH_DEVICE_ID_KEY);
    return value && NOTIFICATION_DEVICE_ID_PATTERN.test(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

/** Creates the id once and keeps it; a browser that refuses storage gets one for this page only. */
export function ensureWebPushDeviceId(
  storage: Pick<Storage, "getItem" | "setItem"> | undefined = browserStorage(),
  createId: () => string = randomDeviceId,
): string {
  const existing = readWebPushDeviceId(storage);
  if (existing) return existing;
  const id = `web-${createId()}`;
  try {
    storage?.setItem(WEB_PUSH_DEVICE_ID_KEY, id);
  } catch {
    // Private mode: registering still works, but the next launch gets another id.
  }
  return id;
}

function randomDeviceId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  if (cryptoApi?.getRandomValues) {
    const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function readWebPushPromptDismissed(
  storage: Pick<Storage, "getItem"> | undefined = browserStorage(),
): boolean {
  try {
    return storage?.getItem(WEB_PUSH_PROMPT_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function rememberWebPushPromptDismissed(
  storage: Pick<Storage, "setItem"> | undefined = browserStorage(),
): void {
  try {
    storage?.setItem(WEB_PUSH_PROMPT_DISMISSED_KEY, "1");
  } catch {
    // Private mode: the offer comes back next session instead of breaking the app.
  }
}

/** The base64url key from the VAPID RPC as the bytes `applicationServerKey` expects. */
export function applicationServerKey(publicKey: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (publicKey.length % 4)) % 4);
  const base64 = `${publicKey}${padding}`.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

/** What the browser is showing now: a live subscription, no permission, or an explicit refusal. */
export async function checkWebPush(
  registration: WebPushRegistration,
  permission: Pick<WebPushPermissionApi, "permission">,
): Promise<WebPushClientState> {
  if (permission.permission === "denied") return "denied";
  try {
    return (await registration.pushManager.getSubscription()) ? "enabled" : "off";
  } catch {
    return "off";
  }
}

/**
 * Asks for permission (inside the click that started it), subscribes with the server's VAPID
 * key, and registers the browser. An existing subscription is reused, so pressing enable twice
 * does not create a second device.
 */
export async function enableWebPush(deps: EnableWebPushDeps): Promise<EnableWebPushResult> {
  try {
    let permission = deps.permission.permission;
    if (permission === "default") permission = await deps.permission.requestPermission();
    if (permission !== "granted") return { status: "denied" };
    const publicKey = await deps.fetchPublicKey();
    if (!publicKey) return { status: "unavailable" };
    const existing = await deps.registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await deps.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey(publicKey),
      }));
    const parsed = WebPushSubscriptionSchema.safeParse(subscription.toJSON());
    if (!parsed.success) return { status: "failed" };
    const deviceId = ensureWebPushDeviceId(deps.storage, deps.createId);
    await deps.registerDevice({ kind: "webpush", deviceId, subscription: parsed.data });
    return { status: "enabled", deviceId };
  } catch {
    return { status: "failed" };
  }
}

/** Removes the browser subscription and tells the server, so the device stops receiving now. */
export async function disableWebPush(deps: DisableWebPushDeps): Promise<boolean> {
  let ok = true;
  try {
    const subscription = await deps.registration.pushManager.getSubscription();
    if (subscription && !(await subscription.unsubscribe())) ok = false;
  } catch {
    ok = false;
  }
  const deviceId = readWebPushDeviceId(deps.storage);
  if (deviceId) {
    try {
      await deps.unregisterDevice({ deviceId });
    } catch {
      ok = false;
    }
  }
  return ok;
}

/**
 * Re-registers a subscription that already exists, e.g. after the server dropped the device
 * when the session ended. Cheap enough to run on every launch.
 */
export async function syncWebPushRegistration(deps: {
  registration: WebPushRegistration;
  permission: Pick<WebPushPermissionApi, "permission">;
  registerDevice(input: RegisterNotificationDeviceInput): Promise<unknown>;
  storage?: WebPushStorage;
  createId?: () => string;
}): Promise<boolean> {
  if (deps.permission.permission !== "granted") return false;
  try {
    const subscription = await deps.registration.pushManager.getSubscription();
    if (!subscription) return false;
    const parsed = WebPushSubscriptionSchema.safeParse(subscription.toJSON());
    if (!parsed.success) return false;
    await deps.registerDevice({
      kind: "webpush",
      deviceId: ensureWebPushDeviceId(deps.storage, deps.createId),
      subscription: parsed.data,
    });
    return true;
  } catch {
    return false;
  }
}
